import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { ArrowRight } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../forms/Button';
import { CalendarEvent, CalendarView } from './Calendar';

const useEventClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 8,
        borderRadius: 4,
        backgroundColor: theme.colors.neutral[4],
        borderLeft: '4px solid currentColor',
        ...theme.fonts.labelSemiBold,
        '&.selectable': { cursor: 'pointer' },
        '&.dragging': {
            padding: '6px 6px 6px 8px',
            borderRight: '2px solid currentColor',
            borderTop: '2px solid currentColor',
            borderBottom: '2px solid currentColor',
        },
    },
});

/**
 * Component that renders the base container for the calendar event.
 * If a render function is not provided through the `event.render` property, the component renders the event name.
 * Setting the `dragging` property to `true` will render a small colored border around the element, that can also be
 * achieved by setting a `dragging` class at the root element.
 *
 * @param event event to render
 * @param view the calendar view, or if it is a tooltip
 * @param tooltip indicates the event is being rendered inside a tooltip
 * @param dragging set styles for dragging
 * @param rootProps root container props
 */
export const Event = ({
    event,
    view,
    tooltip,
    dragging,
    ...rootProps
}: {
    event: CalendarEvent;
    view: CalendarView;
    tooltip?: boolean;
    dragging?: boolean;
} & JSX.IntrinsicElements['div']) => {
    const classes = useEventClasses();
    const cls = { dragging, selectable: !!rootProps.onClick };

    return (
        <div
            {...rootProps}
            className={clsx(classes.root, rootProps.className, cls)}
            style={{ color: event.color, ...rootProps.style }}
        >
            {event.render?.({ event, view, tooltip: !!tooltip }) ?? event.name}
        </div>
    );
};

const useEventListClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: 300,
        maxHeight: 450,
        borderRadius: 4,
        backgroundColor: 'white',
        boxShadow: '0 16px 24px 0 #3031331A',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 16px 10px 16px',
        color: theme.colors.neutral[2],
        ...theme.fonts.h6,
        textTransform: 'uppercase',
        '& > button': { padding: '8px 16px' },
    },
    events: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: 4,
        padding: '0 16px 16px 16px',
        overflow: 'auto',
    },
});

/**
 * Component to display a list of calendar events.
 *
 * @param props.date date to display in the header
 * @param props.events events to display in the component
 * @param props.action custom action on the list header
 */
export const EventList = (props: {
    date: Date;
    events: React.ReactNode | React.ReactNode[];
    action?: { label: string; onClick: () => void };
}) => {
    const classes = useEventListClasses();
    const { t } = usePrefixTranslation('global', { global: 'calendar.Event.EventList' });

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <span>{t('day', { date: props.date })}</span>
                {props.action && (
                    <Button theme='blue' onClick={props.action.onClick}>
                        {props.action.label}
                        <ArrowRight />
                    </Button>
                )}
            </div>
            <div className={clsx(classes.events, 'scroll-shadow')}>{props.events}</div>
        </div>
    );
};
